<template>
  <div class="chat-field" :class="{'chat-field-disabled': disabledtextbox }">
    <div class="chat-field-container">
      <!-- Here are the suggestions -->
      <transition name="chat-field-suggestions-animation">
        <div v-if="query.length == 0" class="chat-field-suggestions">
          <slot />
        </div>
      </transition>
      <span v-if="agentTyping" class="agent-typing">Agent is typing </span>
      <div class="errorMessage" v-show="showError">{{ errorMessage }}</div>
      <div class="chat-field-flexible">
        <!-- Text input -->
        <input
          v-model="query"
          class="chat-field-input"
          type="text"
          autofocus
          :placeholder="$t('inputTitle')"
          :aria-label="$t('inputTitle')"
          :disabled=this.disabledtextbox
          @keyup.enter="submit({ text: query })"
          @input="$emit('input')"
          @blur="$emit('blur')"
          @focus="
            microphone = false;
            should_listen = false;
            $emit('typing');
          "
        />
        <!-- Search input -->
        <button v-if="enableEmoji" class="chat-field-action emoji-button" @click="showModal">
          <emoticon-outline :size="21" />
        </button>

        <FileUploader
            :showUploader="showUploader"
            @upload="uploadFiles"
            @error="setError"
            @click="deleteError"
        ></FileUploader>

        <button
            key="send"
            class="chat-field-action chat-field-send"
            :title="$t('sendTitle')"
            :aria-label="$t('sendTitle')"
            :disabled="disabled"
            @click="submit({ text: query })"
        >
          <arrow-up class="arrow-icon" :size="17" />
        </button>
        <button class="chat-field-action more-button" @click="$emit('openAdditionalModal')">
          <dots-vertical :size="21" />
        </button>

        <EmojiPicker
            v-if="showEmojiModal && enableEmoji"
            @select="onSelectEmoji"
            :emojis="emojis"
            class="emoji-wrapper"
            v-click-outside="(ev) => clickOutside(ev)"/>
      </div>
    </div>
  </div>
</template>

<script>
import AudioRecorder from 'audio-recorder-polyfill'
import translations from '@/translations/translations.json'
import * as hark from 'hark'
import EmojiPicker from '@/components/common/EmojiPicker.vue'
import FileUploader from '@/components/common/FileUploader.vue'
import { enableEmoji, emojiLocationSet } from '@/config'
import { mapState } from 'pinia'
import { useWidgetStore } from '@/store'
import EmoticonOutline from 'vue-material-design-icons/EmoticonOutline.vue'
import ArrowUp from 'vue-material-design-icons/ArrowUp.vue'
import DotsVertical from 'vue-material-design-icons/DotsVertical.vue'
import debounce from 'lodash/debounce'
import ModalComponent from '@/components/common/ModalComponent'

window.MediaRecorder = AudioRecorder

export default {
  name: 'ChatField',
  emits: ['listening'],
  components: {
    FileUploader,
    EmojiPicker,
    EmoticonOutline,
    ArrowUp,
    DotsVertical,
    ModalComponent
  },
  data () {
    return {
      searchInput: false,
      query: '',
      microphone: false,
      recognition: null,
      recorder: null,
      should_listen: false,
      disabled: false,
      translations,
      showEmojiModal: false,
      enableEmoji: enableEmoji,
      showError: false,
      errorMessage: '',
    }
  },
  props: {
    lang: {
      type: String,
      default: null
    },
    translate: {
      type: Function
    },
    showUploader: {
      type: Boolean,
      default: false
    },
    disabledtextbox: {
      type: Boolean,
      default: false
    },
    agentTyping: {
      type: Boolean,
      default: false
    }
    // enableSoftSearch: {
    //   type: Boolean,
    //   default: false
    // }
  },
  computed: {
    ...mapState(useWidgetStore, ['brandingColor', 'enableSoftSearch']),
    microphone_supported () {
      return window.webkitSpeechRecognition || window.SpeechRecognition || !window.MediaRecorder.notSupported
    },
    emojis () {
      const country = localStorage.getItem('country_code')
      return emojiLocationSet[country] || emojiLocationSet.default
    },
    // lang () {
    //   return localStorage.getItem('lang')
    // },
    countryCode () {
      return localStorage.getItem('country_code')
    }
  },
  watch: {
    /* Toggle microphone */
    microphone (activate) {
      if (activate) {
        this.should_listen = true
        this.$emit('listening')

        if (window.webkitSpeechRecognition || window.SpeechRecognition) {
          // eslint-disable-next-line new-cap
          this.recognition = new window.webkitSpeechRecognition() || new window.SpeechRecognition()
          this.recognition.interimResults = true
          this.recognition.lang = this.lang
          this.recognition.onresult = event => {
            for (let i = event.resultIndex; i < event.results.length; ++i) {
              this.query = event.results[i][0].transcript // <- push results to the Text input
            }
          }

          this.recognition.onend = () => {
            this.recognition.stop()
            this.microphone = false
            this.submit({ text: this.query }) // <- submit the result
          }

          this.recognition.onerror = () => {
            this.microphone = false
          }

          this.recognition.start()
        } else if (window.MediaRecorder) {
          navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
              this.recorder = new window.MediaRecorder(stream)
              this.recorder.addEventListener('dataavailable', recording => {
                const reader = new FileReader()
                reader.readAsDataURL(recording.data)
                reader.onloadend = () => {
                  this.submit({ audio: reader.result.replace(/^data:.+;base64,/, '') })
                }
              })

              hark(this.recorder.stream).on('stopped_speaking', () => {
                this.microphone = false
              }) // <- Speech end detection

              this.recorder.start()
            })
            .catch(() => {
              this.microphone = false
            })
        }
      } else if (this.recognition) this.recognition.abort()
      else if (this.recorder) this.recorder.stop()
    }
  },
  methods: {
    listen () {
      if (this.should_listen) this.microphone = true
    },
    clickOutside (ev) {
      if (this.showEmojiModal && ev.target.classList.value !== 'material-design-icon__svg') {
        this.showEmojiModal = false
      }
    },
    onSelectEmoji (emoji) {
      this.query += emoji
    },
    showModal () {
      this.showEmojiModal = !this.showEmojiModal
    },
    submit (submission) {
      if (submission.text && submission.text.length > 0) {
        this.$emit('submit', submission)
        this.query = ''
      } else if (submission.audio) this.$emit('submit', submission)
    },
    uploadFiles (data) {
      const params = { fileList: data, files: true }
      this.$emit('submit', params)
    },
    setError (message) {
      this.errorMessage = message
      this.showError = true
      setTimeout(() => {
        this.errorMessage = ''
        this.showError = false
      }, 3000)
    },
    deleteError () {
      this.errorMessage = ''
      this.showError = false
    },
  }
}
</script>

<style lang="sass" scoped>
@import '@/style/mixins'

.chat-field
  width: 100%
  background-color: var(--background)
  z-index: 2
.chat-field-disabled
  background-color: #e9e8e8
  pointer-events: none
  opacity: 0.6

.errorMessage
  font-size: 12px
  color: #e30a0a
  padding: 5px 0

.errorMessage.tfErrorText
  color: #f2171c

.emoji-wrapper
  position: absolute
  z-index: 10000
  bottom: 62px
  right: 80px

.chat-field-container
  max-width: var(--container-width)
  margin: auto auto
  padding: 10px 18px

  .search-result-popup
    bottom: 48px
    padding: 8px
    height: auto
    .search-result-container
      padding: 7px
      position: relative
      padding-bottom: 50px
      ul
        list-style-type: none
        padding-left: 0
        margin: 3px 0
        max-height: 280px
        overflow-y: auto
        li
          padding: 6px 8px
          border: 0.5px solid #dcdcdc
          font-size: 11px
          line-height: 16px
        li.label
          background-color: #eee
          font-size: 14px
          color: #000
          display: block
        li.search-suggestions
          cursor: pointer

        .no-results
          text-align: center
      .btn-container
        position: absolute
        bottom: 0
        background: white
        width: 100%
        text-align: center
        overflow: hidden
        left:0
        .primary-button
          height: 32px
          background-color: #fff
          border: 1px solid #dcdcdc
          border-radius: 3px
          font-size: 12px
          font-weight: normal
          color: #1e8ae7
          padding: 4px 10px
          margin: 10px
          cursor: pointer
        .close-button
          background-color: #E71316 !important
          color: #fff !important
          background-image: none

  .agent-typing
    font-size: 12px
    color: var(--text-secondary)
    margin: 5px 2px
    display: inline-block

  .agent-typing:after
    overflow: hidden
    display: inline-block
    vertical-align: bottom
    -webkit-animation: ellipsis steps(4, end) 900ms infinite
    animation: ellipsis steps(4, end) 900ms infinite
    content: '...'
    width: 0

.chat-field-flexible
  display: flex
  align-items: center
  justify-content: center
  height: 100%

:deep(.fa-paperclip path)
  fill: var(--text-secondary)

.chat-field-suggestions
  max-height: 200px
  overflow: auto
  margin: 8px

.chat-field-input
  margin-right: 10px
  font-size: 13px
  height: 32px
  width: 100%
  border: var(--border)
  padding: 10px
  color: var(--text-primary)
  background-color: transparent
  outline: none
  margin-bottom: 0

.chat-field-input:active, .chat-field-input:focus
  box-shadow: none !important
  border-color: #c9c8c8 !important

.chat-field-input::placeholder
  font-size:13px !important
  color: #999

  &:disabled
    cursor: not-allowed

  :deep::placeholder
    font-size: 13px !important

.chat-field-action
  @include reset
  padding: 0
  cursor: pointer
  color: #fff
  flex-shrink: 0
  align-items: center
  justify-content: center
  width: 20px
  height: 20px
  margin-right: 4px
  border-radius: 50%
  background-color: v-bind('brandingColor')

  &.chat-field-send
    margin-left: 2px
    height: 18px

  &.more-button
    background-color: transparent
    color: #1E8AE7
    margin-bottom: 1px

  &.emoji-button
    color: var(--text-secondary)
    background-color: transparent

  .arrow-icon svg
    width: 20px !important
    height: 20px !important

  &:disabled
    cursor: not-allowed

  &.mic_active
    color: #F44336

.chat-field-suggestions-animation-enter-active, .chat-field-suggestions-animation-leave-active
  transition: all .15s var(--animation-timing)

.chat-field-suggestions-animation-enter, .chat-field-suggestions-animation-leave-to
  transform: translateY(10px)
  opacity: 0

.chat-field-button-animation-enter-active, .chat-field-button-animation-leave-active
  transition: all .15s var(--animation-timing)

.chat-field-button-animation-enter, .chat-field-button-animation-leave-to
  transform: scale(0)
  opacity: 0

@keyframes ellipsis
  to
    width: 1.25em

@-webkit-keyframes ellipsis
  to
    width: 1.25em

</style>
